import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { getConfirmDeleteDialog } from "./confirmDeleteDialog";
import { Alert } from "@mui/material";
import {
  CssBaseline,
  Box,
  Button,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Snackbar,
  MenuItem,
  Typography,
  Grid,
  ThemeProvider,
  Chip,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { Add, Search } from "@mui/icons-material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FlagIcon from "@mui/icons-material/Flag";
import CloseIcon from "@mui/icons-material/Close";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import moment from "moment";
import Table_theme from "../Themes/TableTheme";
import { ReactComponent as DeleteIcon } from "../Icons/Delete.svg";
import { useCurrentUser } from "../utils/useCurrentUser";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;
const apiBaseUrl = `${BASE_URL}/api/leave-request`;

const statusChip = (status) => {
  const s = (status || "").toLowerCase().trim();
  if (s === "approved")
    return <Chip label="Approved" size="small" sx={{ bgcolor: "#e8f5e9", color: "#2e7d32", fontWeight: 600 }} />;
  if (s === "rejected")
    return <Chip label="Rejected" size="small" sx={{ bgcolor: "#ffebee", color: "#c62828", fontWeight: 600 }} />;
  if (s === "cancelled")
    return <Chip label="Cancelled" size="small" sx={{ bgcolor: "#f5f5f5", color: "#616161", fontWeight: 600 }} />;
  if (s === "cancellation rejected")
    return <Chip label="Cancellation Rejected" size="small" sx={{ bgcolor: "#ffebee", color: "#c62828", fontWeight: 600 }} />;
  if (s.startsWith("cancellation pending") || (s.startsWith("waiting for") && s.includes("cancellation")))
    return <Chip label="Cancellation Pending" size="small" sx={{ bgcolor: "#fff3e0", color: "#e65100", fontWeight: 600 }} />;
  if (s.startsWith("pending, "))
    return <Chip label={status} size="small" sx={{ bgcolor: "#e3f2fd", color: "#1565c0", fontWeight: 600 }} />;
  if (s === "waiting for pm approval")
    return <Chip label="Awaiting PM" size="small" sx={{ bgcolor: "#e3f2fd", color: "#1565c0", fontWeight: 600 }} />;
  if (s === "waiting for hr approval")
    return <Chip label="Awaiting HR" size="small" sx={{ bgcolor: "#e3f2fd", color: "#1565c0", fontWeight: 600 }} />;
  if (s === "waiting for admin approval")
    return <Chip label="Awaiting Admin" size="small" sx={{ bgcolor: "#f3e5f5", color: "#6a1b9a", fontWeight: 600 }} />;
  if (s === "waiting for system admin approval")
    return <Chip label="Awaiting System Admin" size="small" sx={{ bgcolor: "#f3e5f5", color: "#6a1b9a", fontWeight: 600 }} />;
  // "pending" = waiting for TL
  return <Chip label="Awaiting TL" size="small" sx={{ bgcolor: "#fff8e1", color: "#e65100", fontWeight: 600 }} />;
};

const leaveFields = [
  "employee_name",
  "leave_type",
  "leave_description",
  "approval_level_required",
  "approver_id",
  "duration_type",
  "start_date",
  "end_date",
  "total_days",
  "start_time",
  "end_time",
  "total_hours",
   "status",
  "submitted_at",
  "last_updated",
  "emergency_contact",
   "alternative_resource",
];

const popupFields = [
  "employee_id",
  "leave_type",
  "leave_description",
  "approval_level_required",
  "start_date",
  "end_date",
  "total_days",
  "start_time",
  "end_time",
  "total_hours",
  "emergency_contact",
];

const filterFieldMap = {
  employee_name: "employee_id",
  leave_type: "leave_type",
  status: "status",
  approver_id: "approver_name",
  alternative_resource: "alternative_resource_name",
  total_days: "total_days",
  total_hours: "total_hours",
  start_date: "start_date",
  end_date: "end_date",
  start_time: "start_time",
  end_time: "end_time",
  approval_level_required: "approval_level_required",
  duration_type: "duration_type",
};

const fieldLabels = {};
leaveFields.forEach((f) => {
  fieldLabels[f] = f
    .replace(/_/g, " ")
    .replace(/\b\w/g, (l) => l.toUpperCase());
});
fieldLabels.bo_id = "Organization Id";
fieldLabels.employee_id = "Employee Name";
fieldLabels.employee_name = "Employee Name";
fieldLabels.leave_type = "Leave Type";
fieldLabels.approver_id = "Approver Name";

const rowsPerPage = 10;

const LeaveRequestDialogue = ({ open, onClose, employeeId, employeeName }) => {
  const { userId, empId, boId, roles, hasPermission } = useCurrentUser();

  // table + pagination
  const [requests, setRequests] = useState([]);
  const [alertMsg, setAlertMsg] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("success");
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [approvalMap, setApprovalMap] = useState({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [loadingApprovers, setLoadingApprovers] = useState(false);

  // cancel leave state
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelReasonError, setCancelReasonError] = useState("");
  const [cancelLoading, setCancelLoading] = useState(false);

  // filters & search
  const [filters, setFilters] = useState({ status: "All" });
  const [search, setSearch] = useState("");
  const [anchorEls, setAnchorEls] = useState({});
  const [columnAnchorEl, setColumnAnchorEl] = useState(null);
  const [projectResources, setProjectResources] = useState([]);

  const confirmDelete = (request) => {
    if (!request || !request.request_id) {
      console.error("Invalid request object passed to delete handler");
      return;
    }
    setSelectedRequest(request);
    setDeleteDialogOpen(true);
  };
  const fetchProjectResourcesForEmployee = useCallback(
    async (targetEmployeeId) => {
      try {
        if (!targetEmployeeId) {
          setProjectResources([]);
          return;
        }
        const res = await axios.get(
          `${apiBaseUrl}/project-resources/${targetEmployeeId}`,
          { params: { bo_id: boId } }
        );
        setProjectResources(res.data || []);
      } catch (err) {
        console.error("Error fetching project resources:", err);
        setSnackbarMsg("Failed to fetch project resources");
        setShowSnackbar(true);
        setProjectResources([]);
      }
    },
    [boId],
  );

  // columns visibility
  const [columnVisibility, setColumnVisibility] = useState(
    leaveFields.reduce((acc, f) => ({ ...acc, [f]: true }), {}),
  );

  // add/edit form state
  const [activeForm, setActiveForm] = useState(null);

  const [newRequest, setNewRequest] = useState({
    duration_type: "Full/Multiple Days",
    bo_id: boId,
    employee_id: employeeId,
    leave_type: "",
    leave_description: "",
    approval_level_required: null,
    approver_id: null,
    start_date: null,
    end_date: null,
    total_days: null,
    start_time: null,
    end_time: null,
    total_hours: null,
    is_half_day: false,
    emergency_contact: null,
    is_draft: false,
    alternative_resource: null,
  });
  const [editRequest, setEditRequest] = useState({});

  // dropdowns
  const [boOptions, setBoOptions] = useState([]);
  const [boToEmployeesMap, setBoToEmployeesMap] = useState({});
  const [leaveTypeOptions, setLeaveTypeOptions] = useState([]);
  const [leaveTypeLoading, setLeaveTypeLoading] = useState(false);

  // misc
  const [errors, setErrors] = useState({});
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [dialogError, setDialogError] = useState("");

  // fetch paginated requests
  const fetchRequests = useCallback(async () => {
    try {
      // map filters to backend names
      const mappedFilters = {};
      Object.keys(filters).forEach((k) => {
        if (filters[k]) mappedFilters[filterFieldMap[k] || k] = filters[k];
      });
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        employee_id: employeeId,
        ...mappedFilters,
      };
      const res = await axios.get(`${apiBaseUrl}/paginated`, { params });
      setRequests(res.data.data || []);
      setTotalCount(res.data.total || 0);
    } catch (err) {
      setSnackbarMsg("Failed to fetch requests");
      setShowSnackbar(true);
    }
  }, [page, filters, search, boId, empId, roles]);

  const fetchApprovers = useCallback(async () => {
    setLoadingApprovers(true);
    try {
      if (!requests.length) return;
      const map = {};
      await Promise.all(
        requests.map(async (r) => {
          const res = await axios.get(
            `${apiBaseUrl}/approval-history/${r.request_id}`,
            {
              params: { bo_id: boId },
            },
          );
          const approverName = res.data?.[0]?.approver_name || "";
          map[r.request_id] = approverName;
        }),
      );
      setApprovalMap(map);
    } catch (err) {
      console.error("Failed to fetch approvers", err);
    } finally {
      setLoadingApprovers(false);
    }
  }, [requests, boId]);

  useEffect(() => {
    fetchApprovers();
  }, [requests, fetchApprovers]);

  useEffect(
    () => {
      if (!newRequest.employee_id) return;
      axios
        .get(`${apiBaseUrl}/approval-level/${newRequest.employee_id}`, {
          params: { bo_id: boId },
        })
        .then((res) => {
          const level = res.data.approval_level_required || "";
          setNewRequest((prev) => ({
            ...prev,
            approval_level_required: level,
          }));
        })
        .catch((err) => {
          console.error("Error fetching approval level (add):", err);
        });
    },
    [newRequest.employee_id, boId],
    activeForm,
  );
  useEffect(() => {
    if (alertMsg) {
      const timer = setTimeout(() => {
        setAlertMsg("");
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [alertMsg]);

  useEffect(() => {
    if (!editRequest.employee_id) return;
    axios
      .get(`${apiBaseUrl}/approval-level/${editRequest.employee_id}`, {
        params: { bo_id: boId },
      })
      .then((res) => {
        const level = res.data.approval_level_required || "";
        setEditRequest((prev) => ({
          ...prev,
          approval_level_required: level,
        }));
      })
      .catch((err) => {
        console.error("Error fetching approval level (edit):", err);
      });
  }, [editRequest.employee_id, boId]);
  // fetch BO dropdowns and leave types
  const fetchDropdowns = useCallback(async () => {
    try {
      const normalizeRoles = (roles) => {
        if (!roles) return [];
        return Array.isArray(roles)
          ? roles.map((r) => r.toLowerCase())
          : [roles.toLowerCase()];
      };

      const normalizedRoles = normalizeRoles(roles);
      const isAdmin = normalizedRoles.includes("admin");

      const params = !isAdmin ? { bo_id: boId } : {};
      const res = await axios.get(`${apiBaseUrl}/bo-dropdown-options`, {
        params,
      });
      const boSet = new Set();
      const map = {};
      res.data.forEach((row) => {
        if (row.bo_id) {
          boSet.add(row.bo_id);
          if (!map[row.bo_id]) map[row.bo_id] = [];
          map[row.bo_id].push({
            employee_id: row.employee_id,
            first_name: row.first_name,
            last_name: row.last_name,
          });
        }
      });
      setBoOptions([...boSet]);
      setBoToEmployeesMap(map);
    } catch (err) {
      console.error("Error fetching BO options:", err);
      setSnackbarMsg("Failed to fetch organization options");
      setShowSnackbar(true);
    }

    setLeaveTypeLoading(true);
    try {
      const leaveRes = await axios.get(`${apiBaseUrl}/leave-types`);
      const leaveTypes = leaveRes.data.client_type || [];
      if (!Array.isArray(leaveTypes) || !leaveTypes.length) {
        setSnackbarMsg("No leave types available. Contact administrator.");
        setShowSnackbar(true);
      }
      setLeaveTypeOptions(leaveTypes);
    } catch (err) {
      console.error("Error fetching leave types:", err);
      setSnackbarMsg("Failed to fetch leave types");
      setShowSnackbar(true);
    } finally {
      setLeaveTypeLoading(false);
    }
  }, [boId, roles]);

  useEffect(() => {
    if (open) {
      fetchDropdowns();
      fetchRequests();
      fetchProjectResourcesForEmployee(empId || employeeId);
    }
  }, [
    open,
    fetchDropdowns,
    fetchRequests,
    fetchProjectResourcesForEmployee,
    empId,
    employeeId,
  ]);

  useEffect(() => {
    setPage(0);
  }, [search, filters]);

  useEffect(() => {
    if (open) {
      fetchRequests();
    }
  }, [fetchRequests, open, employeeId]);

  // Auto-calculate start_time/end_time from shift when half_day_period changes
  useEffect(() => {
    const reqEmpId = newRequest.employee_id || empId || employeeId;
    const reqBoId  = newRequest.bo_id || boId;
    if (!newRequest.is_half_day || !newRequest.half_day_period || !reqEmpId) return;
    fetch(`${BASE_URL}/commonVariables/shift-timings/${reqBoId}/${reqEmpId}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data) return;
        const times = newRequest.half_day_period === "First Half" ? data.first_half : data.second_half;
        const [sh, sm] = times.start.split(':').map(Number);
        const [eh, em] = times.end.split(':').map(Number);
        const diffMins = (eh * 60 + em) - (sh * 60 + sm);
        const totalHours = diffMins > 0 ? parseFloat((diffMins / 60).toFixed(2)) : null;
        setNewRequest(prev => ({ ...prev, start_time: times.start, end_time: times.end, total_hours: totalHours }));
      })
      .catch(console.error);
  }, [newRequest.half_day_period, newRequest.is_half_day, newRequest.employee_id]);

  useEffect(() => {
    const reqEmpId = editRequest.employee_id || empId || employeeId;
    const reqBoId  = editRequest.bo_id || boId;
    if (!editRequest.is_half_day || !editRequest.half_day_period || !reqEmpId) return;
    fetch(`${BASE_URL}/commonVariables/shift-timings/${reqBoId}/${reqEmpId}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data) return;
        const times = editRequest.half_day_period === "First Half" ? data.first_half : data.second_half;
        const [sh, sm] = times.start.split(':').map(Number);
        const [eh, em] = times.end.split(':').map(Number);
        const diffMins = (eh * 60 + em) - (sh * 60 + sm);
        const totalHours = diffMins > 0 ? parseFloat((diffMins / 60).toFixed(2)) : null;
        setEditRequest(prev => ({ ...prev, start_time: times.start, end_time: times.end, total_hours: totalHours }));
      })
      .catch(console.error);
  }, [editRequest.half_day_period, editRequest.is_half_day, editRequest.employee_id]);

  const calculateTotalDays = (startDate, endDate) => {
    if (!startDate || !endDate) return 0;
    // Use moment to parse as plain dates (no timezone shift)
    const start = moment(startDate, "YYYY-MM-DD", true).isValid()
      ? moment(startDate, "YYYY-MM-DD")
      : moment(startDate);
    const end = moment(endDate, "YYYY-MM-DD", true).isValid()
      ? moment(endDate, "YYYY-MM-DD")
      : moment(endDate);
    return end.diff(start, "days") + 1;
  };

  const calculateTotalHours = (startTime, endTime) => {
    if (!startTime || !endTime) return null;
    // --- Parse "HH:MM" + optional "AM/PM"
    const parseTime = (timeStr) => {
      const parts = timeStr.trim().split(" ");
      const [timePart, modifier] = parts;
      let [hours, minutes] = timePart.split(":").map(Number);
      // Convert 12-hour times to 24-hour
      if (modifier) {
        const mod = modifier.toUpperCase();
        if (mod === "PM" && hours < 12) hours += 12;
        if (mod === "AM" && hours === 12) hours = 0;
      }
      return { hours, minutes };
    };
    const { hours: startHours, minutes: startMinutes } = parseTime(startTime);
    const { hours: endHours, minutes: endMinutes } = parseTime(endTime);

    // --- Create Date objects on the same day
    const start = new Date(0, 0, 0, startHours, startMinutes);
    const end = new Date(0, 0, 0, endHours, endMinutes);

    // --- Handle overnight case (e.g., 10 PM → 6 AM)
    if (end <= start) end.setDate(end.getDate() + 1);

    const diffMs = end - start;
    const diffHours = diffMs / (1000 * 60 * 60);

    return Number(diffHours.toFixed(2));
  };

  const formatDays = (days) => {
    if (days === null || days === undefined) return "";
    const totalDays = Math.round(days); // round to nearest integer
    return totalDays === 1 ? "1" : `${totalDays} `;
  };
  const formatHours = (hours) => {
    if (hours === null || hours === undefined) return "";
    const whole = Math.floor(hours);
    const minutes = Math.round((hours - whole) * 60);
    if (minutes > 0)
      return `${whole} hour${whole !== 1 ? "s" : ""} ${minutes} minute${
        minutes !== 1 ? "s" : ""
      }`;
    return `${whole} hour${whole !== 1 ? "s" : ""}`;
  };

  // field change handlers for add/edit
  const handleNewChange = (field, value) => {
    let updated = { ...newRequest, [field]: value };

    // When leave type changes, auto-set duration_type
    if (field === "leave_type") {
      const isPermission = (value || "").toLowerCase() === "permission";
      const isHalfDay = (value || "").toLowerCase() === "half day leave";
      if (isPermission) {
        updated = { ...updated, duration_type: "Specific Hours", end_date: null, total_days: null, is_half_day: false, half_day_period: null };
      } else if (isHalfDay) {
        updated = { ...updated, duration_type: "Half Day", end_date: null, total_days: null, is_half_day: true, start_time: null, end_time: null, total_hours: null };
      } else {
        updated = { ...updated, duration_type: "Full/Multiple Days", start_time: null, end_time: null, total_hours: null, is_half_day: false, half_day_period: null };
      }
    }

    if (field === "start_date" || field === "end_date") {
      if (updated.duration_type === "Full/Multiple Days") {
        updated.total_days = calculateTotalDays(
          field === "start_date" ? value : updated.start_date,
          field === "end_date" ? value : updated.end_date,
        );
      }
    }
    if (updated.total_days) updated.total_days = Math.round(updated.total_days);
    if (field === "start_time" || field === "end_time") {
      updated.total_hours = calculateTotalHours(
        field === "start_time" ? value : updated.start_time,
        field === "end_time" ? value : updated.end_time,
      );
    }

    setNewRequest(updated);
    setErrors((p) => ({ ...p, [field]: "" }));
    if (field === "employee_id") {
      fetchProjectResourcesForEmployee(value);
    }
  };

  const handleEditChange = (field, value) => {
    let updated = { ...editRequest, [field]: value };

    // When leave type changes, auto-set duration_type
    if (field === "leave_type") {
      const isPermission = (value || "").toLowerCase() === "permission";
      const isHalfDay = (value || "").toLowerCase() === "half day leave";
      if (isPermission) {
        updated = { ...updated, duration_type: "Specific Hours", end_date: null, total_days: null, is_half_day: false, half_day_period: null };
      } else if (isHalfDay) {
        updated = { ...updated, duration_type: "Half Day", end_date: null, total_days: null, is_half_day: true, start_time: null, end_time: null, total_hours: null };
      } else {
        updated = { ...updated, duration_type: "Full/Multiple Days", start_time: null, end_time: null, total_hours: null, is_half_day: false, half_day_period: null };
      }
    }

    if (field === "start_date" || field === "end_date") {
      if (updated.duration_type === "Full/Multiple Days") {
        updated.total_days = calculateTotalDays(
          field === "start_date" ? value : updated.start_date,
          field === "end_date" ? value : updated.end_date,
        );
        if (updated.total_days) {
          updated.total_days = Math.floor(updated.total_days);
        }
      }
    }
    if (field === "start_time" || field === "end_time") {
      updated.total_hours = calculateTotalHours(
        field === "start_time" ? value : updated.start_time,
        field === "end_time" ? value : updated.end_time,
      );
    }

    setEditRequest(updated);
    setErrors((p) => ({ ...p, [field]: "" }));
    if (field === "employee_id") {
      fetchProjectResourcesForEmployee(value);
    }
  };

  // validation (same as uploaded page)
  const validateRequest = (request) => {
    const errs = {};
    const requiredFields = [
      "bo_id",
      "employee_id",
      "leave_type",
      "leave_description",
      "approval_level_required",
      "start_date",
    ];
    if (request.duration_type === "Full/Multiple Days")
      requiredFields.push("end_date", "total_days");
    else if (request.duration_type === "Half Day") {
      if (!request.half_day_period) errs.half_day_period = "Please select First Half or Second Half";
    } else requiredFields.push("start_time", "end_time", "total_hours");

    requiredFields.forEach((f) => {
      if (!request[f] && request[f] !== 0 && request[f] !== false) {
        errs[f] = `${fieldLabels[f] || f} is required`;
      }
    });

    if (
      request.start_date &&
      request.end_date &&
      request.duration_type === "Full/Multiple Days"
    ) {
      const s = moment(request.start_date, "YYYY-MM-DD");
      const e = moment(request.end_date, "YYYY-MM-DD");
      if (e.isBefore(s))
        errs.end_date = "End date must be on or after start date";
    }

    if (
      request.start_time &&
      request.end_time &&
      request.duration_type === "Specific Hours"
    ) {
      const [sh, sm] = (request.start_time || "").split(":").map(Number);
      const [eh, em] = (request.end_time || "").split(":").map(Number);
      const sDate = new Date(0, 0, 0, sh || 0, sm || 0);
      const eDate = new Date(0, 0, 0, eh || 0, em || 0);
      if (eDate <= sDate) errs.end_time = "End time must be after start time";
    }

    return errs;
  };
  const validateDatesAndTimes = (
    startDate,
    endDate,
    startTime,
    endTime,
    durationType,
  ) => {
    let errors = {};

    // Date validation
    if (!startDate) errors.start_date = "Start date is required";
    if (!endDate) errors.end_date = "End date is required";
    if (startDate && endDate) {
      const sDate = moment(startDate, "YYYY-MM-DD");
      const eDate = moment(endDate, "YYYY-MM-DD");
      if (eDate.isBefore(sDate)) errors.end_date = "End date must be after start date";
    }

    // Time validation (only for Specific Hours)
    if (durationType === "Specific Hours") {
      if (!startTime) errors.start_time = "Start time is required";
      if (!endTime) errors.end_time = "End time is required";
      if (startTime && endTime) {
        const start = new Date(`1970-01-01T${startTime}`);
        const end = new Date(`1970-01-01T${endTime}`);
        if (end <= start) errors.end_time = "End time must be after start time";
      }
    }
    return errors;
  };

  const fetchApprovalLevel = async (empId) => {
    try {
      const res = await axios.get(`${apiBaseUrl}/approval-level/${empId}`, {
        params: { bo_id: boId },
      });
      const level = res.data.approval_level_required || "";
      setNewRequest((prev) => ({
        ...prev,
        approval_level_required: level,
      }));
    } catch (err) {
      if (err.response) {
        // Server responded with a status code outside 2xx
        console.error("Approval level fetch failed with response:", {
          status: err.response.status,
          data: err.response.data,
          headers: err.response.headers,
        });
      } else if (err.request) {
        // Request was made but no response received
        console.error(
          "Approval level fetch made but no response:",
          err.request,
        );
      } else {
        // Something else caused the error
        console.error("Error setting up approval level fetch:", err.message);
      }
      console.error("Full error object:", err);
    }
  };

  // Add new request
  const handleAddRequest = async () => {
    const errorsFound = validateRequest(newRequest);
    if (Object.keys(errorsFound).length) {
      setErrors(errorsFound);
      setAlertMsg("Please fill all required fields correctly!");
      setAlertSeverity("error");
      return;
    }

    const payload = {
      ...newRequest,
      created_by: empId,
      start_date: newRequest.start_date
        ? moment(newRequest.start_date).format("YYYY-MM-DD")
        : null,
      end_date: newRequest.end_date
        ? moment(newRequest.end_date).format("YYYY-MM-DD")
        : null,
      start_time: newRequest.start_time ? `${newRequest.start_time}:00` : null,
      end_time: newRequest.end_time ? `${newRequest.end_time}:00` : null,
      total_days: newRequest.total_days || null,
      total_hours: newRequest.total_hours || null,
      approver_id: newRequest.approver_id || null,
      is_half_day: newRequest.is_half_day || false,
      half_day_period: newRequest.is_half_day ? (newRequest.half_day_period || null) : null,
    };

    if (payload.total_days) payload.total_days = Math.round(payload.total_days);

    try {
      const response = await axios.post(apiBaseUrl, payload);

      if (!response.data || !response.data.request_id) {
        throw new Error("Request not inserted");
      }
      await new Promise((r) => setTimeout(r, 300));
      setPage(0);
      await fetchRequests();
      await fetchApprovers();

      setAlertMsg("Request added successfully!");
      setAlertSeverity("success");
      // reset and refresh
      const normalizeRoles = (roles) => {
        if (!roles) return [];
        return Array.isArray(roles)
          ? roles.map((r) => r.toLowerCase())
          : [roles.toLowerCase()];
      };

      const normalizedRoles = normalizeRoles(roles);
      const isAdmin = normalizedRoles.includes("admin");

      const resetForm = {
        duration_type: "Full/Multiple Days",
        bo_id: !isAdmin ? boId || "" : "",
        employee_id: !isAdmin ? empId || employeeId || "" : "",
        leave_type: "",
        leave_description: "",
        approval_level_required: null,
        approver_id: null,
        start_date: null,
        end_date: null,
        total_days: null,
        start_time: null,
        end_time: null,
        total_hours: null,
        is_half_day: false,
        half_day_period: null,
        emergency_contact: null,
        is_draft: false,
      };
      setNewRequest(resetForm);
      if (!resetForm.employee_id) {
        console.warn(
          "empId is missing, cannot fetch approval level:",
          resetForm.employee_id,
        );
        return;
      }
      await fetchApprovalLevel(resetForm.employee_id);
      setActiveForm(null);
    } catch (err) {
      console.error("Add failed:", err.response?.data || err.message);
      setDialogError(err.response?.data?.error || "Failed to add request");
      setAlertSeverity("error");
    }
  };

  // Update request
  const handleUpdateRequest = async () => {
    const errorsFound = validateRequest(editRequest);
    if (Object.keys(errorsFound).length) {
      setErrors(errorsFound);
      setAlertMsg("Please fill all required fields correctly!");
      setAlertSeverity("error");
      return;
    }
    const {
      alternative_resource_name,
      employee_name,
      approver_name,
      approver_remarks,
      ...cleanData
    } = editRequest;

    const payload = {
      ...cleanData,
      bo_id: boId,
      modified_by: empId,
      start_date: cleanData.start_date
        ? moment(cleanData.start_date).format("YYYY-MM-DD")
        : null,
      end_date: cleanData.end_date
        ? moment(cleanData.end_date).format("YYYY-MM-DD")
        : null,
      start_time: cleanData.start_time ? `${cleanData.start_time}:00` : null,
      end_time: cleanData.end_time ? `${cleanData.end_time}:00` : null,
      total_days: cleanData.total_days || null,
      total_hours: cleanData.total_hours || null,
      alternative_resource: cleanData.alternative_resource,
      approver_id: cleanData.approver_id || null,
      is_half_day: cleanData.is_half_day || false,
      half_day_period: cleanData.is_half_day ? (cleanData.half_day_period || null) : null,
    };
    console.log("Update payload:", payload);
    try {
      const res = await axios.put(
        `${apiBaseUrl}/${cleanData.request_id}`,
        payload,
        {
          params: { bo_id: boId },
        },
      );
      setAlertMsg("Request updated successfully!");
      setAlertSeverity("success");
      await fetchRequests();
      setEditRequest({});
      setActiveForm(null);
      setErrors({});
    } catch (err) {
      console.error("Update failed:", err.response?.data || err.message);

      setAlertMsg("Failed to Update request");
      setAlertSeverity("error");
    }
  };

  // delete
  const handleDelete = async (id) => {
    try {
      const res = await axios.delete(`${apiBaseUrl}/${id}`, {
        params: { bo_id: boId },
      });
      if (res.status === 200) {
        setAlertMsg("Request deleted successfully..");
        setAlertSeverity("success");
        await fetchRequests();
      }
    } catch (err) {
      console.error("Delete failed:", err.response?.data || err.message);
      // Check if backend sent a custom message
      if (err.response && err.response.data && err.response.data.error) {
        setAlertMsg(err.response.data.error); // shows "linked to projects" message
      } else {
        setAlertMsg("Something went wrong");
      }
      setAlertSeverity("error");
    }
  };

  // toggle flag (delete_flag)
  const handleToggleFlag = async (request_id) => {
    try {
      const res = await axios.patch(
        `${apiBaseUrl}/${request_id}/toggle-flag`,
        {},
        { params: { bo_id: boId } },
      );
      if (res.status === 200) {
        setAlertMsg("Toggle flag updated successfully..");
        setAlertSeverity("Success");
        await fetchRequests();
      }
    } catch (err) {
      console.error("Toggle failed:", err.response?.data || err.message);
      setAlertMsg("Failed to Flagg Toggled");
      setAlertSeverity("error");
    }
  };

  // Cancel leave request
  const handleCancelLeave = async () => {
    if (!cancelReason.trim()) {
      setCancelReasonError("Cancellation reason is required");
      return;
    }
    if (cancelReason.trim().length > 500) {
      setCancelReasonError("Cancellation reason must be 500 characters or less");
      return;
    }
    setCancelLoading(true);
    try {
      const res = await axios.post(
        `${apiBaseUrl}/${cancelTarget.request_id}/cancel`,
        {
          employee_id: empId,
          bo_id: boId,
          cancellation_reason: cancelReason.trim(),
        }
      );
      setAlertMsg(res.data.message || "Leave request cancelled successfully");
      setAlertSeverity("success");
      setCancelDialogOpen(false);
      setCancelTarget(null);
      setCancelReason("");
      setCancelReasonError("");
      await fetchRequests();
    } catch (err) {
      setAlertMsg(err.response?.data?.error || "Failed to cancel leave request");
      setAlertSeverity("error");
    } finally {
      setCancelLoading(false);
    }
  };

  const commonFieldProps = {
    size: "small",
    fullWidth: true,
    InputLabelProps: { shrink: true },
    sx: { minWidth: 220, m: 1 },
  };

  const renderField = (field, state, onChange, localErrors) => {
    // duration_type is now implicit (driven by leave_type) — never render it
    if (field === "duration_type") return null;

    const isSpecificHours = (state.leave_type || "").toLowerCase() === "permission";

    // Required fields — duration_type removed, driven by leave_type now
    const requiredFields = [
      "leave_type",
      "leave_description",
      "approval_level_required",
      "start_date",
    ];

    if (isSpecificHours) {
      requiredFields.push("start_time", "end_time", "total_hours");
    } else {
      requiredFields.push("end_date", "total_days");
    }

    const isRequired = requiredFields.includes(field);
    const labelText = `${fieldLabels[field] || field}${
      isRequired ? " " : " (Optional)"
    }`;

    const isEmployeeDropdown = field === "employee_id";
    const isLeaveTypeDropdown = field === "leave_type";
    const isDateField = field === "start_date" || field === "end_date";
    const isEndDate = field === "end_date";
    const isTimeField = ["start_time", "end_time"].includes(field);
    const isNumber = [
      "approval_level_required",
      "total_days",
      "total_hours",
    ].includes(field);

    const isHalfDayMode = (state.duration_type || "").toLowerCase() === "half day";

    // Don't render these fields for Specific Hours or Half Day
    if ((isSpecificHours || isHalfDayMode) && ["end_date", "total_days"].includes(field))
      return null;

    // Don't render time fields for Full/Multiple Days; show them (read-only) for Half Day
    if (
      !isSpecificHours && !isHalfDayMode &&
      ["start_time", "end_time", "total_hours"].includes(field)
    )
      return null;

    const handleChange = (field, value) => {
      onChange(field, value);
    };

    // Approval level field (read-only)
    if (field === "approval_level_required") {
      return (
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            {...commonFieldProps}
            key={field}
            label={labelText}
            value={state[field] || ""}
            InputProps={{ readOnly: true }}
            required={isRequired}
            sx={{
              width: "230px",
              "& .MuiOutlinedInput-notchedOutline legend": {
                maxWidth: "130px", // adjust as needed
              },
            }}
          />
        </Grid>
      );
    }

    if (isEmployeeDropdown) {
      return (
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            {...commonFieldProps}
            label={labelText}
            value={employeeName || ""}
            onChange={(e) => handleChange(field, e.target.value)} // optional, can remove if read-only
            error={!!localErrors[field]}
            helperText={localErrors[field]}
            required={isRequired}
            sx={{
              width: "230px",
              "& .MuiOutlinedInput-notchedOutline legend": {
                maxWidth: "135px",
              },
            }}
            InputProps={{
              readOnly: true, // makes it a read-only textbox
            }}
          />
        </Grid>
      );
    }
    if (field === "leave_description") {
      return (
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            {...commonFieldProps}
            key={field}
            label={labelText}
            value={state[field] || ""}
            required={isRequired}
            onChange={(e) => {
              handleChange(field, e.target.value);
              setErrors((p) => ({ ...p, [field]: "" }));
            }}
            error={!!localErrors[field]}
            helperText={localErrors[field]}
            InputLabelProps={{ shrink: true }}
            sx={{
              width: "230px",
              "& .MuiOutlinedInput-notchedOutline legend": {
                maxWidth: "120px",
              },
            }}
          />
        </Grid>
      );
    }

    if (field === "total_days") {
      return (
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            {...commonFieldProps}
            key={field}
            label={labelText}
            value={state[field] || ""}
            InputProps={{
              readOnly: true,
              sx: {
                height: 38,
                fontSize: 14,
                padding: "0 12px",
              },
            }}
            required={isRequired}
            sx={{
              width: "230px",
              mt: 1,
              "& .MuiOutlinedInput-notchedOutline legend": {
                maxWidth: "115px",
              },
            }}
          />
        </Grid>
      );
    }
    
    if (field === "total_hours") {
      return (
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            {...commonFieldProps}
            key={field}
            label={labelText}
            value={state[field] || ""}
            InputProps={{
              readOnly: true,
              sx: {
                height: 38,
                fontSize: 14,
                padding: "0 12px",
              },
            }}
            required={isRequired}
            sx={{
              width: "230px",
              mt: 1,
              "& .MuiOutlinedInput-notchedOutline legend": {
                maxWidth: "115px",
              },
            }}
          />
        </Grid>
      );
    }
    if (isLeaveTypeDropdown) {
      return (
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            {...commonFieldProps}
            select
            label={labelText}
            value={state[field] || ""}
            onChange={(e) => handleChange(field, e.target.value)}
            error={!!localErrors[field]}
            helperText={localErrors[field]}
            disabled={leaveTypeLoading}
            required={isRequired}
            sx={{
              minWidth: 230,
              mt: 1,
              "& .MuiOutlinedInput-notchedOutline legend": {
                maxWidth: "70px",
              },
            }}
          >
            {leaveTypeOptions.map((opt) => (
              <MenuItem
                key={opt.variable_value}
                value={opt.variable_value}
                sx={{ fontSize: "12px" }}
              >
                {opt.variable_value}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      );
    }

    if (isDateField) {
      const isHalfDayMode = (state.duration_type || "").toLowerCase() === "half day";
      // Hide end_date for half day and specific hours
      if (field === "end_date" && (isSpecificHours || isHalfDayMode)) return null;

      return (
        <React.Fragment key={field}>
          <Grid item xs={12} sm={6} md={3}>
            <DatePicker
              label={fieldLabels[field] || field}
              value={state[field] ? moment(state[field], "YYYY-MM-DD") : null}
              onChange={(newValue) => {
                const formatted = newValue ? moment(newValue).format("YYYY-MM-DD") : null;
                let updatedValue = formatted;
                if (field === "start_date" && state.end_date) {
                  if (moment(formatted).isAfter(moment(state.end_date))) {
                    onChange("end_date", null);
                  }
                }
                if (field === "end_date" && state.start_date) {
                  if (moment(formatted).isBefore(moment(state.start_date))) {
                    updatedValue = state.start_date;
                  }
                }
                onChange(field, updatedValue);
              }}
              minDate={
                field === "end_date" && state.start_date
                  ? moment(state.start_date)
                  : moment()
              }
              shouldDisableDate={(date) => {
                const day = date.day();
                return day === 0 || day === 6;
              }}
              format="MM/DD/YYYY"
              mask="__/__/____"
              slotProps={{
                textField: {
                  size: "small",
                  sx: {
                    height: 35,
                    maxWidth: 230,
                    mt: 1,
                    mr: 0.5,
                    ...(field === "start_date" && { ml: 0 }),
                    ...(field === "end_date" && { ml: "-4px", mr: 0 }),
                  },
                  error: !!localErrors[field],
                  helperText: localErrors[field],
                  InputLabelProps: { shrink: true },
                },
              }}
            />
          </Grid>
          {/* Morning/Afternoon selector — only for Half Day mode after start_date */}
          {field === "start_date" && isHalfDayMode && (
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                label="Half Day Period *"
                value={state.half_day_period || ""}
                onChange={(e) => onChange("half_day_period", e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
                error={!!localErrors?.half_day_period}
                helperText={localErrors?.half_day_period || ""}
                sx={{ height: 35, maxWidth: 230, mt: 1, ml: 0 }}
              >
                <MenuItem value="First Half">First Half</MenuItem>
                <MenuItem value="Second Half">Second Half</MenuItem>
              </TextField>
            </Grid>
          )}
        </React.Fragment>
      );
    }

    if (field === "emergency_contact") {
      return (
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            {...commonFieldProps}
            key={field}
            label={labelText}
            value={state[field] || ""}
            onChange={(e) => handleChange(field, e.target.value)}
            size="small"
            required={isRequired}
            sx={{
              width: "230px",
              mt: 1,
              "& .MuiOutlinedInput-notchedOutline legend": {
                maxWidth: "150px",
              },
            }}
            error={!!errors[field]}
            helperText={errors[field]}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
      );
    }

    return (
      <Grid item xs={12} sm={6} md={4}>
        <TextField
          {...commonFieldProps}
          type={isTimeField ? "time" : isNumber ? "number" : "text"}
          label={labelText}
          value={state[field] || ""}
          onChange={(e) => handleChange(field, e.target.value)}
          sx={{
            width: "230px",
            mb: 1,
            mt: 1,
            "& .MuiInputLabel-root": {
              transform: "translate(12px, -7px) scale(0.75)",
              lineHeight: "1",
              whiteSpace: "nowrap",
            },
            "& .MuiOutlinedInput-notchedOutline legend": {
              maxWidth: isTimeField ? "80px" : "90px",
            },
          }}
          InputProps={{
            readOnly: (isNumber && ["total_days", "total_hours"].includes(field)) || (isTimeField && isHalfDayMode),
            ...(isTimeField ? { inputProps: { step: 300 } } : {}),
          }}
          InputLabelProps={{ shrink: true }}
          error={!!localErrors[field]}
          helperText={localErrors[field]}
          required={isRequired}
        />
      </Grid>
    );
  };

  // small helper to get employee name from boToEmployeesMap
  const getEmployeeName = (row) => {
    if (!row) return "";
    const map = boToEmployeesMap[row.bo_id] || [];
    const emp = map.find((e) => e.employee_id === row.employee_id);
    if (emp) return `${emp.first_name} ${emp.last_name}`;
    return row.employee_id || "";
  };
  const getAlternativeResourceName = (empId) => {
    if (!empId) return "";
    const emp = projectResources.find((res) => res.employee_id === empId);
    return emp ? `${emp.first_name} ${emp.last_name}` : empId;
  };

  const getApproverName = (row) => {
    if (!row || !row.approver_id) return "";
    const map = boToEmployeesMap[row.bo_id] || [];
    const approver = map.find((e) => e.employee_id === row.approver_id);
    if (approver) return `${approver.first_name} ${approver.last_name}`;
    return row.approver_id; // fallback id
  };

  return (
    <Grid item xs={12} sm={6} md={4}>
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <Dialog
          open={open}
          onClose={(_, reason) => {
            if (reason === "backdropClick") return;
            onClose();
          }}
          fullWidth
          maxWidth="lg"
        >
          <DialogTitle
            sx={{
              borderRadius: "12px",
              background: "linear-gradient(45deg, #e6f0fa, #ffffff)",
              p: 2,
              fontSize: "14px",
              fontWeight: 550,
            }}
          >
            Leave Requests for {employeeName}
            <IconButton
              aria-label="close"
              onClick={onClose}
              sx={{ position: "absolute", right: 10, top: 10 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ p: 3 }}>
            {!activeForm && (
              <Box
                mb={3}
                display="flex"
                justifyContent="flex-end"
                alignItems="center"
                gap={1}
              >
                {(() => {
                  const normalizeRoles = (roles) => {
                    if (!roles) return [];
                    return Array.isArray(roles)
                      ? roles.map((r) => r.toLowerCase())
                      : [roles.toLowerCase()];
                  };
                  const normalizedRoles = normalizeRoles(roles);
                  const isAdmin = normalizedRoles.includes("admin");
                  return (
                    !isAdmin || (isAdmin && empId === newRequest.employee_id)
                  );
                })() && (
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => {
                      setActiveForm("add");
                      setNewRequest((prev) => ({
                        ...prev,
                        bo_id: boId,
                        employee_id: empId,
                      }));
                    }}
                    sx={{ mt: 1, px: 2, py: 1 }}
                  >
                    Add Leave Request
                  </Button>
                )}
              </Box>
            )}

            {activeForm === "add" && (
              <Box mb={2}>
                {dialogError && (
                  <Alert severity="error" sx={{ mb: 1 }} onClose={() => setDialogError("")}>
                    {dialogError}
                  </Alert>
                )}
                <Grid container spacing={2} mt={1}>
                  {popupFields.map((field, index) => (
                    <React.Fragment key={field}>
                      {renderField(field, newRequest, handleNewChange, errors)}
                    </React.Fragment>
                  ))}
                  <Grid item xs={12} sx={{ textAlign: "right" }}>
                    <Button
                      variant="contained"
                      onClick={handleAddRequest}
                      sx={{ mr: 1, px: 4, py: 1, ml: 3, mt: 0.8 }}
                    >
                      Save
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setActiveForm(null);
                        setErrors({});
                        setDialogError("");
                      }}
                      sx={{ px: 3, py: 1, mt: 0.8 }}
                    >
                      Cancel
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            )}

            {activeForm === "edit" && (
              <Box mb={2}>
                <Grid container spacing={2} mt={1}>
                  {popupFields.map((field) => (
                    <React.Fragment key={field}>
                      {renderField(
                        field,
                        editRequest,
                        handleEditChange,
                        errors,
                      )}
                    </React.Fragment>
                  ))}
                  <Grid item xs={12} sx={{ textAlign: "right" }}>
                    <Button
                      variant="contained"
                      onClick={handleUpdateRequest}
                      sx={{ mr: 1, px: 4, py: 1, ml: 3, mt: 0.8 }}
                    >
                      Update
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setActiveForm(null);
                        setEditRequest({});
                        setErrors({});
                      }}
                      sx={{ px: 3, py: 1, mt: 0.8 }}
                    >
                      Cancel
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            )}

            {alertMsg && (
              <Box mb={2}>
                <Alert
                  onClose={() => setAlertMsg("")}
                  variant="standard"
                  sx={{
                    color: alertSeverity === "success" ? "#2E9F50" : "#FF0C0C",
                    fontWeight: 500,
                    backgroundColor: "#fff",
                  }}
                >
                  {alertMsg}
                </Alert>
              </Box>
            )}

            {/* Table */}
            <ThemeProvider theme={Table_theme}>
              <CssBaseline />
              <TableContainer component={Paper} sx={{ mt: 1 }}>
                <Table sx={{ minWidth: 650 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Actions</TableCell>
                      {leaveFields.map((field) => (
                        <TableCell key={field}>
                          {fieldLabels[field] || field}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {requests.length > 0 ? (
                      requests.map((request) => (
                        <TableRow key={request.request_id || Math.random()}>
                          <TableCell>
                            <Box display="flex" gap={0.5}>
                              <Tooltip
                                title={
                                  ["Approved","Cancelled","Cancellation Rejected"].includes(request.status)
                                    ? "Cannot edit approved request"
                                    : hasPermission("Leave Request", "Edit")
                                      ? "Edit"
                                      : "No permission"
                                }
                              >
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      if (
                                        !hasPermission(
                                          "Leave Request",
                                          "Edit",
                                        ) ||
                                        ["Approved","Cancelled","Cancellation Rejected"].includes(request.status)
                                      )
                                        return;

                                      setEditRequest({
                                        ...request,
                                        // Derive duration_type from leave_type — Permission = Specific Hours
                                        duration_type: (request.leave_type || "").toLowerCase() === "permission"
                                          ? "Specific Hours"
                                          : "Full/Multiple Days",
                                        start_date: request.start_date
                                          ? moment(request.start_date).format("YYYY-MM-DD")
                                          : null,
                                        end_date: request.end_date
                                          ? moment(request.end_date).format("YYYY-MM-DD")
                                          : null,
                                        total_days: request.total_days
                                          ? Math.floor(request.total_days)
                                          : null,
                                        start_time: request.start_time || null,
                                        end_time: request.end_time || null,
                                        total_hours:
                                          request.total_hours || null,
                                        status: request.status || null,
                                        emergency_contact:
                                          request.emergency_contact || null,
                                        alternative_resource:
                                          request.alternative_resource || null,
                                        is_draft: request.is_draft || false,
                                        is_half_day: request.is_half_day || false,
                                        half_day_period: request.half_day_period || null,
                                        approval_level_required:
                                          request.approval_level_required ||
                                          null,
                                        approver_id: request.approver_id || "",
                                      });

                                      setActiveForm("edit");
                                      fetchProjectResourcesForEmployee(
                                        request.employee_id ||
                                          empId ||
                                          employeeId,
                                      );
                                    }}
                                    disabled={
                                      !hasPermission("Leave Request", "Edit") ||
                                      ["Approved","Cancelled","Cancellation Rejected"].includes(request.status)
                                    }
                                    sx={{
                                      color:
                                        hasPermission(
                                          "Leave Request",
                                          "Edit",
                                        ) && request.status !== "Approved"
                                          ? "#2DB0E4"
                                          : "#ccc",
                                    }}
                                  >
                                    <EditOutlinedIcon
                                      sx={{ fontSize: "16px" }}
                                    />
                                  </IconButton>
                                </span>
                              </Tooltip>

                              {/* Delete Button */}
                              <Tooltip
                                title={
                                  ["Approved","Cancelled","Cancellation Rejected"].includes(request.status)
                                    ? "Cannot delete approved request"
                                    : hasPermission("Leave Request", "Delete")
                                      ? "Delete"
                                      : "No permission"
                                }
                              >
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      hasPermission(
                                        "Leave Request",
                                        "Delete",
                                      ) && confirmDelete(request)
                                    }
                                    disabled={
                                      !hasPermission(
                                        "Leave Request",
                                        "Delete",
                                      ) || ["Approved","Cancelled","Cancellation Rejected"].includes(request.status)
                                    }
                                  >
                                    <DeleteIcon
                                      sx={{
                                        color: hasPermission(
                                          "Leave Request",
                                          "Delete",
                                        )
                                          ? "red"
                                          : "#ccc",
                                        width: 15,
                                        height: 15,
                                      }}
                                    />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              {/* Flag Button */}
                              <Tooltip
                                title={
                                  hasPermission("Leave Request", "Edit")
                                    ? "Toggle Delete Flag"
                                    : "No permission"
                                }
                              >
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      hasPermission("Leave Request", "Edit") &&
                                      handleToggleFlag(request.request_id)
                                    }
                                    disabled={
                                      !hasPermission("Leave Request", "Edit")
                                    }
                                  >
                                    <FlagIcon
                                      style={{
                                        color:
                                          request.delete_flag === "N"
                                            ? "#2E9F50"
                                            : "#FF0C0C",
                                        transition: "color 0.2s",
                                        width: 17,
                                        height: 17,
                                        verticalAlign: "middle",
                                      }}
                                    />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              {/* Cancel Button */}
                              {(() => {
                                const s = (request.status || "").toLowerCase();
                                const isCancellationPending = s.startsWith("cancellation pending") || (s.startsWith("waiting for") && s.includes("cancellation"));
                                const isCancelled = s === "cancelled";
                                const isRejected = s === "rejected";

                                // Block cancellation of past approved leaves
                                const endDateStr = request.end_date
                                  ? moment(request.end_date).format("YYYY-MM-DD")
                                  : (request.start_date ? moment(request.start_date).format("YYYY-MM-DD") : null);
                                const todayStr = moment().format("YYYY-MM-DD");
                                const isPastLeave = s === "approved" && endDateStr && endDateStr < todayStr;

                                const isDisabled = isCancelled || isRejected || isCancellationPending || isPastLeave;
                                const tooltipText = isCancelled ? "Leave already cancelled"
                                  : isRejected ? "Leave was rejected"
                                  : isCancellationPending ? "Cancellation already pending"
                                  : isPastLeave ? "Cannot cancel a leave that has already ended"
                                  : "Cancel Leave";

                                return (
                                  <Tooltip title={tooltipText}>
                                    <span>
                                      <IconButton
                                        size="small"
                                        disabled={isDisabled}
                                        onClick={() => {
                                          if (!isDisabled) {
                                            setCancelTarget(request);
                                            setCancelReason("");
                                            setCancelReasonError("");
                                            setCancelDialogOpen(true);
                                          }
                                        }}
                                        sx={{ color: isDisabled ? "#ccc" : "#d32f2f" }}
                                      >
                                        <CancelOutlinedIcon sx={{ fontSize: 18 }} />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                );
                              })()}
                            </Box>
                          </TableCell>

                          {leaveFields.map((field) => (
                            <TableCell key={field}>
                              {[
                                "start_date",
                                "end_date",
                                "submitted_at",
                                "last_updated",
                              ].includes(field) && request[field]
                                ? moment.utc(request[field]).format("MM/DD/YYYY")
                                : field === "status"
                                  ? statusChip(request[field])
                                  : field === "leave_type"
                                    ? request[field]
                                    : field === "employee_name"
                                      ? getEmployeeName(request)
                                      : field === "approver_id"
                                        ? approvalMap?.[request.request_id] ||
                                          request[field] ||
                                          ""
                                        : field === "alternative_resource"
                                          ? getAlternativeResourceName(
                                              request.alternative_resource_name,
                                            )
                                          : field === "total_hours"
                                            ? formatHours(request[field])
                                            : field === "total_days"
                                              ? formatDays(request[field])
                                              : request[field]}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={leaveFields.length + 1}
                          align="center"
                        >
                          No leave requests found.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                mt={2}
                mb={1}
                sx={{ width: "100%", overflowX: "auto", paddingBottom: "8px" }}
              >
                <Button
                  size="medium"
                  onClick={() => setPage((prev) => Math.max(prev - 1, 0))}
                  disabled={page === 0}
                  sx={{
                    minWidth: 36,
                    fontSize: "1rem",
                    fontWeight: "bold",
                    color: "#000",
                  }}
                >
                  &lt;
                </Button>
                <Box
                  sx={{
                    backgroundColor: "#add8e6",
                    color: "#fff",
                    width: 28,
                    height: 28,
                    borderRadius: "6px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: "normal",
                    fontSize: "0.8rem",
                  }}
                >
                  {page + 1}
                </Box>
                <Button
                  size="medium"
                  onClick={() =>
                    setPage((prev) =>
                      prev + 1 < Math.ceil(totalCount / rowsPerPage)
                        ? prev + 1
                        : prev,
                    )
                  }
                  disabled={page + 1 >= Math.ceil(totalCount / rowsPerPage)}
                  sx={{
                    minWidth: 36,
                    fontSize: "1rem",
                    fontWeight: "bold",
                    color: "#1976d2",
                  }}
                >
                  &gt;
                </Button>
              </Box>
              <Typography
                textAlign="center"
                sx={{ fontSize: "0.75rem", mb: 2 }}
              >
                {totalCount === 0
                  ? "0 of 0"
                  : `${page * rowsPerPage + 1} - ${Math.min(
                      (page + 1) * rowsPerPage,
                      totalCount,
                    )} of ${totalCount}`}
              </Typography>
            </ThemeProvider>
          </DialogContent>
          {getConfirmDeleteDialog({
            open: deleteDialogOpen,
            onClose: () => {
              setDeleteDialogOpen(false);
              setSelectedRequest(null);
            },
            onConfirm: () => {
              if (selectedRequest) {
                handleDelete(selectedRequest.request_id);
              }
              setDeleteDialogOpen(false);
              setSelectedRequest(null);
            },
            title: "Confirm Deletion",
            contentText: selectedRequest
              ? `Are you sure you want to delete the request for ${getEmployeeName(
                  selectedRequest,
                )}?`
              : "Are you sure you want to delete this request?",
          })}

          {/* ── Cancel Leave Dialog ── */}
          <Dialog open={cancelDialogOpen} onClose={() => { if (!cancelLoading) { setCancelDialogOpen(false); setCancelTarget(null); setCancelReason(""); setCancelReasonError(""); } }} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              Cancel Leave Request
              <IconButton size="small" onClick={() => { if (!cancelLoading) { setCancelDialogOpen(false); setCancelTarget(null); setCancelReason(""); setCancelReasonError(""); } }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              {cancelTarget && (() => {
                const s = (cancelTarget.status || "").toLowerCase();
                const isApproved = s === "approved";
                // Use moment (no utc) to parse DB dates — date-only, no timezone conversion needed
                const todayStr = moment().format("YYYY-MM-DD");
                const startStr = moment(cancelTarget.start_date).format("YYYY-MM-DD");
                const endStr = moment(cancelTarget.end_date || cancelTarget.start_date).format("YYYY-MM-DD");
                const isSameDay = isApproved && todayStr >= startStr && todayStr <= endStr;
                return (
                  <Box sx={{ mb: 1 }}>
                    {isSameDay && (
                      <Alert severity="warning" sx={{ mb: 2, fontSize: "0.8rem" }}>
                        This leave is active today. Cancellation will require approval from your manager hierarchy.
                      </Alert>
                    )}
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      Please provide a reason for cancelling this leave request.
                    </Typography>
                    <TextField
                      label="Cancellation Reason"
                      multiline
                      rows={3}
                      fullWidth
                      size="small"
                      value={cancelReason}
                      onChange={(e) => { setCancelReason(e.target.value); if (cancelReasonError) setCancelReasonError(""); }}
                      error={!!cancelReasonError}
                      helperText={cancelReasonError || `${cancelReason.length}/500`}
                      inputProps={{ maxLength: 500 }}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Box>
                );
              })()}
            </DialogContent>
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, px: 3, pb: 2 }}>
              <Button variant="outlined" onClick={() => { setCancelDialogOpen(false); setCancelTarget(null); setCancelReason(""); setCancelReasonError(""); }} disabled={cancelLoading}>
                Back
              </Button>
              <Button variant="contained" color="error" onClick={handleCancelLeave} disabled={cancelLoading}>
                {cancelLoading ? "Cancelling..." : "Confirm Cancel"}
              </Button>
            </Box>
          </Dialog>
        </Dialog>
      </LocalizationProvider>
    </Grid>
  );
};

export default LeaveRequestDialogue;
